/**
 * @fileoverview The Excel export of the missing timesheets report
 * (MOR-1460), kept apart from the component so the sheet's exact rows and
 * file name are unit-tested (QA-69: the in-app QA browser cannot capture a
 * download, so the file's contents are proven here instead).
 */

import { format, parseISO } from "date-fns";
import * as XLSX from "xlsx";

import { formatDateRange } from "./format";

export interface MissingPersonRow {
  name: string;
  email: string;
  expectedUnits: number;
  recordedUnits: number;
  shortUnits: number;
  missing: { date: string; shortUnits: number }[];
}

export interface MissingTimesheetsSheet {
  fileName: string;
  /** Rows exactly as they land in the sheet: title, period, blank, header, people. */
  rows: (string | number)[][];
}

const TITLE = "Missing timesheets";

function hours(units: number, unitMinutes: number): number {
  return (units * unitMinutes) / 60;
}

export function buildMissingTimesheetsSheet(
  people: MissingPersonRow[],
  range: { start: string; end: string },
  chasedTo: string,
  unitMinutes: number,
): MissingTimesheetsSheet {
  return {
    fileName: `${TITLE} ${range.start} to ${range.end}.xlsx`,
    rows: [
      [TITLE],
      [
        `${formatDateRange(range.start, range.end)}, chased to ${format(parseISO(chasedTo), "d MMM yyyy")}`,
      ],
      [],
      [
        "Person",
        "Email",
        "Expected (h)",
        "Recorded (h)",
        "Short (h)",
        "Days short",
      ],
      ...people.map((p) => [
        p.name,
        p.email,
        hours(p.expectedUnits, unitMinutes),
        hours(p.recordedUnits, unitMinutes),
        hours(p.shortUnits, unitMinutes),
        p.missing
          .map(
            (d) =>
              `${format(parseISO(d.date), "EEE d MMM")} (${hours(d.shortUnits, unitMinutes).toLocaleString("en-AU", { maximumFractionDigits: 2 })}h short)`,
          )
          .join("; "),
      ]),
    ],
  };
}

/** Download the report as a single-sheet workbook. */
export function exportMissingTimesheets(sheet: MissingTimesheetsSheet): void {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet(sheet.rows),
    "Missing",
  );
  XLSX.writeFile(wb, sheet.fileName.replace(/[/\\?%*:|"<>]/g, "-"));
}
